import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    FaInstagram, FaFacebook, FaPlus, FaSync, FaTrash, FaExclamationTriangle,
    FaChartLine, FaCommentDots, FaUsers, FaHeart, FaTimes,
    FaExternalLinkAlt, FaImage, FaPlay, FaUserCheck, FaEye, FaExchangeAlt, FaPen,
    FaChevronDown, FaMagic, FaRegCalendarAlt, FaTiktok, FaYoutube, FaSearch, FaStar, FaRegStar,
} from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { socialApi, proxyImage } from './api';
import { SOCIAL_READONLY } from './analyticsShared';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, Title, Tooltip, Legend, Filler);

// Plataformas con monitoreo de cuenta (Enfoque B). TikTok admite dos modos: por
// @usuario (perfil real con followers, como IG) o por KEYWORD (#término, agrega
// posts de muchos creadores, histórico). El modo se elige en el modal (track_by).
const PLATFORMS = [
    { id: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#E1306C', ph: 'usuario de Instagram', kind: 'handle' },
    { id: 'facebook', label: 'Facebook', icon: FaFacebook, color: '#1877F2', ph: 'página o URL de grupo de FB', kind: 'handle' },
    { id: 'tiktok', label: 'TikTok', icon: FaTiktok, color: '#010101', ph: '@usuario o URL de TikTok', kind: 'handle' },
    { id: 'youtube', label: 'YouTube', icon: FaYoutube, color: '#FF0000', ph: '@canal o URL de YouTube', kind: 'handle' },
];
// TikTok sigue en beta: solo estos usuarios pueden agregar cuentas de TikTok.
// Para habilitarlo a otro cliente, agrega su username aquí.
const TIKTOK_USERS = [
    'admin-main-demo',
    'mauricio.vargas@cantabrialabs.mx',
];
// Una cuenta es "por keyword" solo si es TikTok con track_by='keyword' (histórico,
// sin perfil, sin filtro de fechas). El resto (incl. TikTok @usuario) es 'profile'.
const isKwAcct = (a) => !!(a && a.platform === 'tiktok' && a.track_by === 'keyword');
// Plataformas con captura async lenta (poll largo + banner ~10 min).
const isSlowPlatform = (p) => p === 'tiktok' || p === 'facebook' || p === 'youtube';

// Tipo de cuenta: propia de la organización vs. de interés (competencia/monitoreo).
const KINDS = {
    own: { key: 'own', label: 'Propia', plural: 'Mis cuentas', Icon: FaUserCheck, color: '#16a34a' },
    interest: { key: 'interest', label: 'De interés', plural: 'Cuentas de interés', Icon: FaEye, color: '#7c3aed' },
};
const kindOf = (a) => (a?.account_kind === 'interest' ? 'interest' : 'own');
// Etiqueta del identificador: '#keyword' para TikTok, '@handle' para el resto.
const acctHandle = (a) => (isKwAcct(a) ? a.username : `@${a?.username}`);
// Etiqueta visible: prioriza el SOBRENOMBRE (alias que puso el usuario); si no hay,
// usa el nombre real (display_name) cuando difiere del handle — p. ej. el nombre de
// un grupo de FB, cuyo username es un ID numérico; y si tampoco, cae a @handle/#keyword.
const acctLabel = (a) => (a && a.nickname ? a.nickname
    : (a && a.display_name && a.display_name !== a.username ? a.display_name : acctHandle(a)));

// Ordenamiento de posts (heredado del módulo TikTok legacy + ER/NSS de Por Cuenta).
const POST_SORTS = [
    { key: 'recent', label: 'Más recientes' },
    { key: 'oldest', label: 'Más antiguas' },
    { key: 'views', label: 'Vistas' },
    { key: 'likes', label: 'Likes' },
    { key: 'comments', label: 'Comentarios' },
    { key: 'er', label: 'Engagement rate' },
    { key: 'nss', label: 'NSS' },
];
const sortPosts = (arr, key) => {
    const a = [...(arr || [])];
    const num = (v) => (typeof v === 'number' ? v : 0);
    const ts = (v) => { const t = v ? new Date(v).getTime() : 0; return Number.isFinite(t) ? t : 0; };
    switch (key) {
        case 'oldest':   // posts sin fecha al final para no contaminar el top
            return a.sort((x, y) => {
                const tx = ts(x.posted_at), ty = ts(y.posted_at);
                if (tx === 0 && ty !== 0) return 1;
                if (ty === 0 && tx !== 0) return -1;
                return tx - ty;
            });
        case 'views': return a.sort((x, y) => num(y.views) - num(x.views));
        case 'likes': return a.sort((x, y) => num(y.likes) - num(x.likes));
        case 'comments': return a.sort((x, y) => num(y.comments_count) - num(x.comments_count));
        case 'er': return a.sort((x, y) => num(y.er) - num(x.er));
        case 'nss': return a.sort((x, y) => num(y.nss) - num(x.nss));
        case 'recent':
        default: return a.sort((x, y) => ts(y.posted_at) - ts(x.posted_at));
    }
};

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (n) => {
    if (n === null || n === undefined) return '—';
    const x = Number(n);
    if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(1) + 'M';
    if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1) + 'K';
    return String(x);
};
const nssColor = (v) => (v == null ? '#94a3b8' : v > 15 ? '#16a34a' : v < -15 ? '#dc2626' : '#d97706');
const sentDot = { pos: '#16a34a', neu: '#d97706', neg: '#dc2626' };
const TONE = { pos: '#16a34a', neu: '#64748b', neg: '#dc2626' };   // color del punto de cada insight
// Presets de visualización (gratis: solo filtran lo ya capturado por fecha).
const RANGES = [
    { key: 'mtd', label: 'Mes en curso' },
    { key: 'lastmonth', label: 'Mes anterior' },
    { key: '30d', label: '30 días' },
    { key: '60d', label: '60 días' },
    { key: '90d', label: '90 días' },
    { key: 'all', label: 'Todo' },
];
// 'YYYY-MM-DD' en hora LOCAL (no UTC) para no desfasar el día.
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Límites {from, to} de cada preset (o el rango custom). to='' = hasta hoy.
const rangeBounds = (key, custom) => {
    if (key === 'custom') return { from: (custom && custom.from) || '', to: (custom && custom.to) || '' };
    const now = new Date();
    if (key === 'mtd') return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: '' };
    if (key === 'lastmonth') return {
        from: ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: ymd(new Date(now.getFullYear(), now.getMonth(), 0)),   // día 0 del mes = último del anterior
    };
    if (key === 'all') return { from: '', to: '' };
    const days = key === '60d' ? 60 : key === '90d' ? 90 : 30;
    const d = new Date(now); d.setDate(d.getDate() - days);
    return { from: ymd(d), to: '' };
};

// Huella de los posts: cambia cuando hay una re-captura (suben likes/comentarios/
// views o aparecen posts nuevos). Es la señal de fin del refresh cuando el backend
// aún no expone posts_synced_at.
const fpPosts = (arr) => (arr || [])
    .map((p) => `${p.id}:${p.likes}:${p.comments_count}:${p.views}`)
    .join('|');

// Lunes (inicio de semana ISO) de una fecha 'YYYY-MM-DD', en UTC.
const weekStart = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00Z');
    const dow = (d.getUTCDay() + 6) % 7;   // 0 = lunes
    d.setUTCDate(d.getUTCDate() - dow);
    return d.toISOString().slice(0, 10);
};

// Serie de NSS por PUBLICACIÓN: agrupa los posts analizados (con nss) por su fecha
// de publicación (día o semana) y promedia su NSS. Así el punto de cada día coincide
// con el badge NSS de sus posts y con el drill-down (mismo criterio: fecha de post).
const buildPostFlow = (posts, bucket) => {
    const groups = {};
    for (const p of posts || []) {
        if (p.nss == null || !p.posted_at) continue;
        const day = p.posted_at.slice(0, 10);
        const key = bucket === 'week' ? weekStart(day) : day;
        if (!groups[key]) groups[key] = { sum: 0, n: 0, analyzed: 0 };
        groups[key].sum += p.nss;
        groups[key].n += 1;
        groups[key].analyzed += (p.comments_analyzed || 0);
    }
    return Object.keys(groups).sort().map((k) => ({
        bucket: k, nss: Math.round((groups[k].sum / groups[k].n) * 100) / 100,
        posts: groups[k].n, analyzed: groups[k].analyzed,
    }));
};

// Aplica un orden personalizado (lista de ids) a las cuentas; las que no estén en
// el orden van al final, conservando el orden del backend (created_at).
const applyOrder = (accounts, order) => {
    const idx = new Map(order.map((id, i) => [id, i]));
    return [...accounts].sort((a, b) =>
        (idx.has(a.id) ? idx.get(a.id) : 1e9) - (idx.has(b.id) ? idx.get(b.id) : 1e9));
};

// Suma n días a una fecha 'YYYY-MM-DD' en UTC (coincide con el slice de los
// timestamps, que vienen en UTC del backend) → evita corrimientos por zona horaria.
const addDaysStr = (d, n) => {
    const t = new Date(d + 'T00:00:00Z');
    t.setUTCDate(t.getUTCDate() + n);
    return t.toISOString().slice(0, 10);
};

// Media móvil centrada (ventana impar) para suavizar la serie de NSS. Trata los
// buckets como puntos consecutivos e ignora nulos dentro de la ventana. Útil sobre
// todo en vista diaria, donde el NSS salta mucho con pocos comentarios por día.
const movingAvg = (vals, window) => {
    if (!window || window <= 1) return vals;
    const half = Math.floor(window / 2);
    return vals.map((_, i) => {
        let sum = 0, n = 0;
        for (let j = i - half; j <= i + half; j++) {
            const v = vals[j];
            if (v != null) { sum += v; n += 1; }
        }
        return n ? Math.round((sum / n) * 100) / 100 : null;
    });
};

// ── Card de post (estilo similar al módulo de TikTok) ───────────────────────
function PostCard({ post, onOpen, onToggleFeatured, onHide }) {
    const [imgOk, setImgOk] = useState(true);
    return (
        <div className="sa-post-card" onClick={() => onOpen(post)}>
            <div className="sa-post-thumb">
                {post.thumbnail && imgOk ? (
                    <img src={proxyImage(post.thumbnail)} alt="" loading="lazy"
                        onError={() => setImgOk(false)} />
                ) : (
                    <div className="sa-post-thumb-ph"><FaImage /></div>
                )}
                <span className="sa-post-type">
                    {post.content_type === 'Reel' && <FaPlay />} {post.content_type || 'Post'}
                    {post.is_paid ? ' 💰' : ''}
                </span>
                {onHide && (
                    <button className="sa-post-hide"
                        title="Ocultar publicación (no volverá a aparecer)"
                        onClick={(e) => { e.stopPropagation(); onHide(post); }}>
                        <FaTrash />
                    </button>
                )}
                {onToggleFeatured && (
                    <button className={`sa-post-star ${post.featured ? 'on' : ''}`}
                        title={post.featured ? 'Quitar de destacadas' : 'Destacar publicación'}
                        onClick={(e) => { e.stopPropagation(); onToggleFeatured(post); }}>
                        {post.featured ? <FaStar /> : <FaRegStar />}
                    </button>
                )}
                {post.nss != null && (
                    <span className="sa-post-nss" style={{ background: nssColor(post.nss) }}>
                        NSS {post.nss}
                    </span>
                )}
            </div>
            <div className="sa-post-body">
                <p className="sa-post-caption">{post.caption || '(sin descripción)'}</p>
                <div className="sa-post-metrics">
                    <span title="Likes"><FaHeart /> {fmt(post.likes)}</span>
                    <span title="Comentarios"><FaCommentDots /> {fmt(post.comments_count)}</span>
                    {post.views != null && <span title="Views"><FaPlay /> {fmt(post.views)}</span>}
                    <span title="Engagement rate" className="sa-post-er">ER {post.er ?? '—'}%</span>
                </div>
                <span className="sa-post-date">{(post.posted_at || '').slice(0, 10)}</span>
            </div>
        </div>
    );
}

// Miniatura pequeña para la tabla de posts, con fallback si la imagen falla.
function PostsThumb({ src }) {
    const [ok, setOk] = useState(true);
    return (
        <span className="sa-posts-thumb">
            {src && ok
                ? <img src={proxyImage(src)} alt="" loading="lazy" onError={() => setOk(false)} />
                : <span className="sa-posts-thumb-ph"><FaImage /></span>}
        </span>
    );
}

// ── Tabla de posts (todas las métricas) — vista alterna del grid ────────────
function PostsTable({ posts, onOpen, onToggleFeatured, onHide }) {
    return (
        <div className="sa-table-wrap">
            <table className="sa-table sa-posts-table">
                <thead>
                    <tr>
                        {onToggleFeatured && <th title="Destacar"><FaStar /></th>}
                        {onHide && <th title="Ocultar"><FaTrash /></th>}
                        <th>#</th>
                        <th>Publicación</th>
                        <th>Fecha</th>
                        <th>Tipo</th>
                        <th title="Likes"><FaHeart /></th>
                        <th title="Comentarios"><FaCommentDots /></th>
                        <th title="Reproducciones"><FaPlay /></th>
                        <th title="Engagement rate">ER</th>
                        <th title="Net Sentiment Score">NSS</th>
                    </tr>
                </thead>
                <tbody>
                    {posts.map((p, i) => (
                        <tr key={p.id} className="sa-posts-row" onClick={() => onOpen(p)}>
                            {onToggleFeatured && (
                                <td>
                                    <button className={`sa-posts-star ${p.featured ? 'on' : ''}`}
                                        title={p.featured ? 'Quitar de destacadas' : 'Destacar'}
                                        onClick={(e) => { e.stopPropagation(); onToggleFeatured(p); }}>
                                        {p.featured ? <FaStar /> : <FaRegStar />}
                                    </button>
                                </td>
                            )}
                            {onHide && (
                                <td>
                                    <button className="sa-posts-hide" title="Ocultar publicación"
                                        onClick={(e) => { e.stopPropagation(); onHide(p); }}>
                                        <FaTrash />
                                    </button>
                                </td>
                            )}
                            <td className="sa-rank">{i + 1}</td>
                            <td>
                                <div className="sa-posts-pub">
                                    <PostsThumb src={p.thumbnail} />
                                    <span className="sa-posts-cap" title={p.caption || ''}>
                                        {p.caption || '(sin descripción)'}
                                    </span>
                                </div>
                            </td>
                            <td className="sa-posts-date">{(p.posted_at || '').slice(0, 10)}</td>
                            <td>{p.content_type || 'Post'}{p.is_paid ? ' 💰' : ''}</td>
                            <td>{fmt(p.likes)}</td>
                            <td>{fmt(p.comments_count)}</td>
                            <td>{p.views != null ? fmt(p.views) : '—'}</td>
                            <td className="sa-posts-er">{p.er ?? '—'}%</td>
                            <td>
                                {p.nss != null
                                    ? <span style={{ color: nssColor(p.nss), fontWeight: 700 }}>{p.nss}</span>
                                    : '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Modal de detalle (preview + sentimiento + comentarios) ──────────────────
function PostModal({ username, platform, post, onClose, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');
    const [imgOk, setImgOk] = useState(true);
    const [msg, setMsg] = useState('');
    const alive = useRef(true);
    // Reiniciar a true en cada montaje (StrictMode monta dos veces en dev; sin
    // esto el ref queda en false y el polling no corre).
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
    const napms = (ms) => new Promise((r) => setTimeout(r, ms));
    const netName = platform === 'facebook' ? 'Facebook' : platform === 'tiktok' ? 'TikTok' : platform === 'youtube' ? 'YouTube' : 'Instagram';

    const load = useCallback(async () => {
        setLoading(true);
        try { setDetail((await socialApi.postDetail(username, post.id)).detail); }
        catch (e) { setMsg(e.message); }
        finally { setLoading(false); }
    }, [username, post.id]);

    useEffect(() => { load(); }, [load]);

    const capture = async () => {
        setBusy('capture'); setMsg('');
        try {
            const resp = await socialApi.refreshComments(username, post.id);
            // Los comentarios se comparten con quien más monitoree la cuenta: si
            // ya están recién capturados, no se vuelve a extraer — solo recargamos.
            if (resp?.status === 'fresh') {
                await load();
                setMsg(resp.message || 'Los comentarios ya están actualizados.');
                return;
            }
            const slow = platform === 'facebook' || platform === 'tiktok';
            setMsg(`Capturando comentarios… ${slow ? 'Facebook puede tardar 2-5 min.' : '(puede tardar 1-3 min)'}`);
            // Auto-poll: recarga el detalle hasta que lleguen comentarios. El
            // proveedor a veces tarda varios minutos, así que poleamos ~6-7 min.
            let found = false;
            const tries = slow ? 40 : 36;
            for (let i = 0; i < tries && alive.current; i++) {
                await napms(10000);
                if (!alive.current) return;
                try {
                    const d = (await socialApi.postDetail(username, post.id)).detail;
                    if (d && (d.comments || []).length) {
                        setDetail(d);
                        setMsg('Comentarios listos. Pulsa "Analizar sentimiento".');
                        found = true;
                        break;
                    }
                } catch (_) { /* reintenta */ }
            }
            // Mensaje terminal: sin esto el aviso "Capturando…" se quedaba para
            // siempre cuando el proveedor no devolvía comentarios (parecía un bucle).
            if (!found && alive.current) {
                setMsg('No llegaron comentarios. El proveedor a veces no los devuelve para ciertos posts (típico en publicaciones con pocos comentarios o muy recientes). Puedes reintentar en un momento.');
            }
        } catch (e) { setMsg(e.message); }
        finally { if (alive.current) setBusy(''); }
    };
    const analyze = async () => {
        setBusy('analyze'); setMsg('');
        try {
            await socialApi.analyzePost(username, post.id);
            await load();
            onChanged && onChanged();
        } catch (e) { setMsg(e.message); }
        finally { setBusy(''); }
    };

    const sent = detail?.sentiment;
    const comments = detail?.comments || [];
    const pv = detail?.post || post;   // detail.post trae likes/comments_count/is_paid
    const total = sent ? (sent.pos + sent.neu + sent.neg) || 1 : 1;
    const pct = (x) => Math.round((x / total) * 100);

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="sa-modal-grid">
                    {/* Preview. `pv` = detail.post (con likes/comments) si ya cargó;
                        si no, el prop `post` (que abierto desde un tema es un puente
                        sin contadores → así la cabecera se completa al cargar). */}
                    <div className="sa-modal-preview">
                        {pv.thumbnail && imgOk ? (
                            <img src={proxyImage(pv.thumbnail)} alt="" onError={() => setImgOk(false)} />
                        ) : <div className="sa-post-thumb-ph big"><FaImage /></div>}
                        <div className="sa-modal-meta">
                            <span className="sa-post-type dark">
                                {pv.content_type || 'Post'}{pv.is_paid ? ' 💰' : ''}
                            </span>
                            <span>{(pv.posted_at || '').slice(0, 10)}</span>
                            <span><FaHeart /> {fmt(pv.likes)} · <FaCommentDots /> {fmt(pv.comments_count)}</span>
                            {pv.url && <a href={pv.url} target="_blank" rel="noreferrer">
                                Ver en {netName} <FaExternalLinkAlt /></a>}
                        </div>
                        <p className="sa-modal-caption">{pv.caption}</p>
                    </div>

                    {/* Sentimiento + comentarios */}
                    <div className="sa-modal-analysis">
                        {!SOCIAL_READONLY && (
                            <div className="sa-modal-actions">
                                <button disabled={busy} onClick={capture}>
                                    {busy === 'capture' ? <FaSync className="spin" /> : <FaCommentDots />} Capturar comentarios
                                </button>
                                <button disabled={busy} className="primary" onClick={analyze}>
                                    {busy === 'analyze' ? <FaSync className="spin" /> : <FaChartLine />} Analizar sentimiento
                                </button>
                            </div>
                        )}
                        {msg && <div className="sa-banner sa-banner-info">{msg}</div>}

                        {sent && (
                            <div className="sa-modal-sent">
                                <div className="sa-sent-head">
                                    <span className="sa-sent-nss" style={{ color: nssColor(sent.nss) }}>
                                        NSS {sent.nss}
                                    </span>
                                    <span className="sa-sent-sub">{sent.comments_analyzed} comentarios · polarización {sent.polarization}</span>
                                </div>
                                <div className="sa-sent-bar">
                                    <div style={{ width: `${pct(sent.pos)}%`, background: sentDot.pos }} />
                                    <div style={{ width: `${pct(sent.neu)}%`, background: sentDot.neu }} />
                                    <div style={{ width: `${pct(sent.neg)}%`, background: sentDot.neg }} />
                                </div>
                                {sent.summary && <p className="sa-sent-summary">{sent.summary}</p>}
                                {Array.isArray(sent.themes) && sent.themes.length > 0 && (
                                    <div className="sa-themes">
                                        {sent.themes.map((t, i) => (
                                            <span key={i} className="sa-theme-chip">{t.name} <em>{t.count}</em></span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="sa-comments">
                            <h5>Comentarios ({comments.length})</h5>
                            {loading && <p className="sa-empty">Cargando…</p>}
                            {!loading && comments.length === 0 && (
                                <p className="sa-empty">Aún no hay comentarios. Pulsa "Capturar comentarios".</p>
                            )}
                            {comments.map((c, i) => (
                                <div key={i} className="sa-comment">
                                    {c.sentiment && (
                                        <span className="sa-comment-dot" style={{ background: sentDot[c.sentiment] }}
                                            title={c.theme || ''} />
                                    )}
                                    <div className="sa-comment-body">
                                        <span className="sa-comment-user">@{c.commenter || 'usuario'}</span>
                                        <span className="sa-comment-text">{c.text}</span>
                                        <span className="sa-comment-meta">
                                            <FaHeart /> {fmt(c.likes)}{c.theme ? ` · ${c.theme}` : ''}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Miniatura del post de origen con fallback si la imagen falla (presigned caducada
// / CDN 403). Subcomponente para tener su propio estado de error por comentario.
function ThemeSrcThumb({ src, onOpen }) {
    const [imgOk, setImgOk] = useState(true);
    return (
        <button className="sa-theme-src" title="Ver publicación de origen" onClick={onOpen}>
            {src && imgOk
                ? <img src={proxyImage(src)} alt="" loading="lazy" onError={() => setImgOk(false)} />
                : <span className="sa-theme-src-ph"><FaImage /></span>}
        </button>
    );
}

// ── Modal de un tema: comentarios del tema + su publicación de origen ───────
function ThemeModal({ username, accountId, theme, dateFrom, dateTo, onOpenPost, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    useEffect(() => {
        let alive = true;
        setLoading(true); setErr('');
        socialApi.themeComments(username, accountId, theme, dateFrom, dateTo || '')
            .then((r) => { if (alive) setData(r.detail); })
            .catch((e) => {
                if (alive) setErr(e.status === 404
                    ? 'Esta vista estará disponible en breve (requiere actualización del servidor).'
                    : e.message);
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [username, accountId, theme, dateFrom, dateTo]);

    const comments = data?.comments || [];
    const s = data?.summary || {};
    const tot = (s.pos + s.neu + s.neg) || 1;
    const pct = (x) => Math.round(((x || 0) / tot) * 100);

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="sa-modal sa-theme-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="sa-theme-modal-body">
                    <div className="sa-theme-head">
                        <h4>Tema: <span className="sa-theme-name">{theme}</span></h4>
                        {data && (
                            <div className="sa-theme-summary">
                                <span className="sa-theme-nss" style={{ color: nssColor(s.nss) }}>NSS {s.nss}</span>
                                <span className="sa-theme-count">{s.total} comentarios</span>
                                <div className="sa-sent-bar">
                                    <div style={{ width: `${pct(s.pos)}%`, background: sentDot.pos }} />
                                    <div style={{ width: `${pct(s.neu)}%`, background: sentDot.neu }} />
                                    <div style={{ width: `${pct(s.neg)}%`, background: sentDot.neg }} />
                                </div>
                            </div>
                        )}
                    </div>

                    {loading && <p className="sa-empty">Cargando comentarios…</p>}
                    {err && <div className="sa-banner sa-banner-info">{err}</div>}
                    {!loading && !err && comments.length === 0 && (
                        <p className="sa-empty">Sin comentarios de este tema en el rango.</p>
                    )}

                    <div className="sa-theme-comments">
                        {comments.map((c, i) => (
                            <div key={i} className="sa-theme-comment">
                                {/* Publicación de origen, a un lado (click → detalle del post) */}
                                <ThemeSrcThumb src={c.thumbnail} onOpen={() => onOpenPost({
                                    id: c.post_pk, url: c.post_url, caption: c.post_caption,
                                    content_type: c.post_type, posted_at: c.post_posted_at,
                                    thumbnail: c.thumbnail,
                                })} />
                                <div className="sa-theme-comment-body">
                                    <div className="sa-theme-comment-top">
                                        {c.sentiment && <span className="sa-comment-dot" style={{ background: sentDot[c.sentiment] }} />}
                                        <span className="sa-comment-user">@{c.commenter || 'usuario'}</span>
                                        <span className="sa-comment-meta"><FaHeart /> {fmt(c.likes)}</span>
                                        <span className="sa-theme-date">{(c.commented_at || '').slice(0, 10)}</span>
                                    </div>
                                    <p className="sa-theme-comment-text">{c.text}</p>
                                    <p className="sa-theme-src-cap" title={c.post_caption || ''}>
                                        📷 {(c.post_caption || '(sin descripción)').slice(0, 90)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Selector de rango: presets + calendario de 2 meses (estilo Cost Explorer) ──
function DateRangeControl({ rangeKey, customRange, onPreset, onCustom }) {
    const [open, setOpen] = useState(false);
    const [sel, setSel] = useState([null, null]);   // [start, end] del calendario
    const ref = useRef(null);

    const label = rangeKey === 'custom'
        ? `${customRange.from} → ${customRange.to || 'hoy'}`
        : (RANGES.find((r) => r.key === rangeKey) || {}).label || 'Rango';

    const openPop = () => {
        const b = rangeBounds(rangeKey, customRange);
        setSel([
            b.from ? new Date(`${b.from}T00:00:00`) : null,
            b.to ? new Date(`${b.to}T00:00:00`) : null,
        ]);
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const [start, end] = sel;
    return (
        <div className="sa-daterange" ref={ref}>
            <button className="sa-daterange-btn" onClick={() => (open ? setOpen(false) : openPop())}>
                <FaRegCalendarAlt /> {label} <FaChevronDown />
            </button>
            {open && (
                <div className="sa-daterange-pop">
                    <div className="sa-daterange-presets">
                        {RANGES.map((r) => (
                            <button key={r.key} type="button"
                                className={rangeKey === r.key ? 'active' : ''}
                                onClick={() => { onPreset(r.key); setOpen(false); }}>
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <div className="sa-daterange-cal">
                        <DatePicker selectsRange inline monthsShown={1}
                            startDate={start} endDate={end} maxDate={new Date()}
                            onChange={(upd) => setSel(upd)} />
                        <div className="sa-daterange-foot">
                            <span className="sa-daterange-hint">
                                {start && end ? `${ymd(start)} → ${ymd(end)}` : 'Elige fecha de inicio y fin'}
                            </span>
                            <div className="sa-daterange-btns">
                                <button type="button" className="sa-dr-cancel" onClick={() => setOpen(false)}>
                                    Cancelar
                                </button>
                                <button type="button" className="sa-dr-apply" disabled={!start || !end}
                                    onClick={() => { onCustom(ymd(start), ymd(end)); setOpen(false); }}>
                                    Aplicar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Modal para agregar cuenta (elige red + tipo + handle) ───────────────────
function AddAccountModal({ initialPlatform, busy, err, platforms = PLATFORMS, onClose, onSubmit }) {
    const [plat, setPlat] = useState(initialPlatform || 'instagram');
    const [kind, setKind] = useState('own');
    const [tkMode, setTkMode] = useState('profile');   // solo TikTok: 'profile' | 'keyword'
    const [value, setValue] = useState('');
    const [nick, setNick] = useState('');              // sobrenombre opcional
    const curP = PLATFORMS.find((x) => x.id === plat) || PLATFORMS[0];
    const PIcon = curP.icon;
    const kwMode = plat === 'tiktok' && tkMode === 'keyword';
    const trackBy = plat === 'tiktok' ? tkMode : 'profile';
    const fieldPh = kwMode ? 'palabra clave o término' : curP.ph;

    const submit = (e) => {
        e.preventDefault();
        if (!value.trim() || busy) return;
        onSubmit(plat, kind, value.trim(), trackBy, nick.trim());
    };

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <form className="sa-modal sa-add-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
                <button type="button" className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="sa-add-modal-body">
                    <h4>Agregar cuenta</h4>

                    <label className="sa-field-label">Red social</label>
                    <div className="sa-platform-switch">
                        {platforms.map((pl) => {
                            const Icon = pl.icon;
                            return (
                                <button key={pl.id} type="button"
                                    className={plat === pl.id ? 'active' : ''}
                                    onClick={() => setPlat(pl.id)}
                                    style={plat === pl.id ? { borderColor: pl.color, color: pl.color } : undefined}>
                                    <Icon /> {pl.label}
                                </button>
                            );
                        })}
                    </div>

                    {plat === 'tiktok' && (
                        <>
                            <label className="sa-field-label">Rastrear por</label>
                            <div className="sa-seg sa-tkmode-seg">
                                <button type="button" className={tkMode === 'profile' ? 'active' : ''}
                                    onClick={() => setTkMode('profile')}>Cuenta (@usuario)</button>
                                <button type="button" className={tkMode === 'keyword' ? 'active' : ''}
                                    onClick={() => setTkMode('keyword')}>Keyword</button>
                            </div>
                        </>
                    )}

                    <label className="sa-field-label">Tipo de cuenta</label>
                    <div className="sa-kind-choose">
                        {Object.values(KINDS).map((k) => {
                            const KIcon = k.Icon;
                            const active = kind === k.key;
                            return (
                                <button key={k.key} type="button"
                                    className={`sa-kind-opt ${active ? 'active' : ''}`}
                                    onClick={() => setKind(k.key)}
                                    style={active ? { borderColor: k.color, color: k.color, background: `${k.color}14` } : undefined}>
                                    <KIcon /> {k.label}
                                </button>
                            );
                        })}
                    </div>

                    <label className="sa-field-label">{kwMode ? 'Keyword' : 'Usuario'}</label>
                    <div className="sa-add sa-add-modal-input">
                        {kwMode
                            ? <FaSearch className="sa-ig-icon" style={{ color: curP.color }} />
                            : <PIcon className="sa-ig-icon" style={{ color: curP.color }} />}
                        <input autoFocus value={value} onChange={(e) => setValue(e.target.value)}
                            placeholder={fieldPh} />
                    </div>

                    <label className="sa-field-label">Sobrenombre <span style={{ color: '#94a3b8', fontWeight: 400 }}>(opcional)</span></label>
                    <div className="sa-add sa-add-modal-input">
                        <FaPen className="sa-ig-icon" style={{ color: curP.color }} />
                        <input value={nick} maxLength={120} onChange={(e) => setNick(e.target.value)}
                            placeholder="Alias para mostrar (útil si el canal no tiene nombre)" />
                    </div>

                    {err && <div className="sa-banner sa-banner-info" style={{ marginTop: 14, marginBottom: 0 }}>{err}</div>}

                    <button type="submit" className="sa-add-submit" disabled={busy}
                        style={{ background: KINDS[kind].color }}>
                        {busy ? <FaSync className="spin" /> : <FaPlus />} Agregar en {KINDS[kind].plural}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function AccountsDashboard({ username }) {
    const [accounts, setAccounts] = useState([]);   // de TODAS las redes
    const [selected, setSelected] = useState(null);
    const [addOpen, setAddOpen] = useState(false);     // modal de alta abierto
    const [addErr, setAddErr] = useState('');          // error mostrado DENTRO del modal de alta
    const [menu, setMenu] = useState(null);            // { id, x, y } del menú de un chip
    const [nickEdit, setNickEdit] = useState(null);    // { id, value } editor de sobrenombre
    const [adding, setAdding] = useState(false);
    // Orden personalizado del carrusel (arrastrar para reordenar), persistido local.
    const [order, setOrder] = useState(() => {
        try { return JSON.parse(localStorage.getItem(`sa_account_order_${username}`) || '[]'); }
        catch (_) { return []; }
    });
    const [dragOverId, setDragOverId] = useState(null);
    const dragId = useRef(null);
    const [kindFilter, setKindFilter] = useState('all');   // 'all' | 'own' | 'interest'
    const [err, setErr] = useState('');
    const [rangeKey, setRangeKey] = useState('mtd');   // visualización: mes en curso por defecto
    const [customRange, setCustomRange] = useState({ from: '', to: '' });   // rango del calendario
    const [overview, setOverview] = useState(null);
    const [posts, setPosts] = useState([]);
    const [destacados, setDestacados] = useState([]);   // publicaciones destacadas (por usuario) de la cuenta activa
    const [flowBucket, setFlowBucket] = useState('day');   // 'week' | 'day' (por publicación) — default: día
    const [smooth, setSmooth] = useState(false);     // suavizar (media móvil) — off: muestra el NSS real por publicación
    const [selectedDay, setSelectedDay] = useState(null);  // día/bucket clickeado en el chart
    const [loadingDash, setLoadingDash] = useState(false);
    const [ingesting, setIngesting] = useState(false);
    const [openPost, setOpenPost] = useState(null);
    const [openTheme, setOpenTheme] = useState(null);   // tema clickeado en "Temas más comentados"
    const [postsView, setPostsView] = useState('table');   // 'table' (default) | 'grid'
    const [postsSort, setPostsSort] = useState('recent');  // orden de la lista de posts
    const mounted = useRef(true);
    const pollToken = useRef(0);   // época del poll activo (un poll nuevo anula al viejo)
    // El proveedor entrega el HISTÓRICO de la cuenta (posts de meses atrás), pero el
    // rango por defecto es el mes en curso: recién capturado, el dashboard salía
    // vacío y parecía que "Actualizar" no había hecho nada. Tras una captura que
    // pidió el usuario ampliamos el rango una vez para que vea lo que se trajo.
    const widenAfterCapture = useRef(false);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const loadAccounts = useCallback(async () => {
        try {
            const r = await socialApi.listAccounts(username);   // todas las redes (sin filtro)
            const accs = r.accounts || [];
            setAccounts(accs);
            // El server es la fuente de verdad del orden una vez guardado: si alguna
            // cuenta trae sort_order, adoptamos el orden que devuelve (ya viene ordenado).
            if (accs.some((a) => a.sort_order != null)) setOrder(accs.map((a) => a.id));
            // Forma funcional: tras un setSelected(null) (p. ej. al quitar la cuenta
            // activa) re-elige la primera disponible sin depender de un closure viejo.
            if (accs.length) setSelected((cur) => cur || accs[0]);
        } catch (e) { setErr(e.message); }
    }, [username]);

    useEffect(() => { loadAccounts(); /* eslint-disable-next-line */ }, [username]);

    // Cierra el menú del chip al hacer click fuera o al hacer scroll.
    useEffect(() => {
        if (!menu) return;
        const close = () => setMenu(null);
        window.addEventListener('click', close);
        window.addEventListener('scroll', close, true);
        return () => {
            window.removeEventListener('click', close);
            window.removeEventListener('scroll', close, true);
        };
    }, [menu]);

    const loadDashboard = useCallback(async () => {
        if (!selected) return;
        setLoadingDash(true);
        setErr('');
        // TikTok-por-keyword trae histórico (no acotado por fecha): mostramos todo.
        const { from, to } = isKwAcct(selected)
            ? { from: '', to: '' } : rangeBounds(rangeKey, customRange);
        try {
            const [ov, ps] = await Promise.all([
                socialApi.overview(username, selected.id, from, to, 'week'),
                socialApi.posts(username, selected.id, from, to),
            ]);
            setOverview(ov.overview);
            setPosts(ps.posts || []);
            // Captura recién pedida que no dejó nada visible en el rango actual, pero
            // la cuenta SÍ tiene posts guardados: son de meses anteriores. Ampliamos a
            // "Todo" (una sola vez) y lo decimos, en vez de dejar la vista en blanco.
            if (widenAfterCapture.current) {
                widenAfterCapture.current = false;
                if ((ps.posts || []).length === 0 && (selected.posts_stored || 0) > 0
                    && rangeKey !== 'all' && !isKwAcct(selected)) {
                    setRangeKey('all');   // vuelve a disparar loadDashboard
                    setErr('La captura trajo publicaciones anteriores a este mes: ampliamos el rango a "Todo" para mostrarlas.');
                }
            }
        } catch (e) { setErr(e.message); }
        finally { setLoadingDash(false); }
    }, [username, selected, rangeKey, customRange]);

    useEffect(() => { loadDashboard(); /* eslint-disable-next-line */ }, [selected, rangeKey, customRange]);

    // Publicaciones destacadas (por usuario) de la cuenta activa — todo el histórico.
    const loadFeatured = useCallback(async () => {
        if (!selected) { setDestacados([]); return; }
        try {
            const r = await socialApi.featuredPosts(username, selected.id);
            setDestacados(r.posts || []);
        } catch (_) { /* sección vacía */ }
    }, [username, selected]);

    useEffect(() => { loadFeatured(); /* eslint-disable-next-line */ }, [selected]);

    // Marca/desmarca un post como destacado (optimista en lista + sección + server).
    const toggleFeatured = (post) => {
        const nv = !post.featured;
        setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, featured: nv } : p)));
        setDestacados((ds) => nv
            ? (ds.some((p) => p.id === post.id) ? ds : [{ ...post, featured: true }, ...ds])
            : ds.filter((p) => p.id !== post.id));
        socialApi.setFeatured(username, post.id, nv).catch(() => { loadDashboard(); loadFeatured(); });
    };

    // Oculta un post: lo quita de la vista (lista + destacadas) y no reaparece al
    // re-scrapear. Optimista; si el server falla, recargamos para revertir.
    const hidePost = (post) => {
        if (!window.confirm('¿Ocultar esta publicación? No volverá a aparecer en el monitoreo, ni siquiera al actualizar.')) return;
        setPosts((ps) => ps.filter((p) => p.id !== post.id));
        setDestacados((ds) => ds.filter((p) => p.id !== post.id));
        socialApi.hidePost(username, post.id, true).catch(() => { loadDashboard(); loadFeatured(); });
    };

    // El flujo de NSS se deriva de los posts cargados (por fecha de publicación),
    // así que solo cerramos el drill-down cuando cambia el contexto del chart.
    useEffect(() => { setSelectedDay(null); setOpenTheme(null); }, [selected, rangeKey, customRange, flowBucket]);

    const pollForData = useCallback(async (targetHandle, opts = {}) => {
        const { sinceSyncedAt = null, beforePosts = null, accountId = null } = opts;
        const plat = opts.platform || null;   // red de la cuenta objetivo (null = cualquiera)
        const handleClean = (targetHandle || '').replace(/^[@#]/, '');
        // Marca de la última captura de posts ANTES de iniciar. En "Actualizar"
        // la cuenta ya tenía posts, así que `posts_stored > 0` ya es verdadero al
        // primer poll y cortaba con datos viejos (había que recargar).
        const sinceMs = sinceSyncedAt ? new Date(sinceSyncedAt).getTime() : 0;
        // Si otra alta/refresh arranca un poll nuevo, éste deja de escribir estado
        // (setSelected/setAccounts/setIngesting) para que gane siempre el más reciente.
        const myToken = ++pollToken.current;
        const alive = () => mounted.current && pollToken.current === myToken;
        setIngesting(true);
        // Facebook y TikTok (keyword) son async y lentos: el scrape del proveedor
        // puede tardar ~10-12 min, así que poleamos hasta ~12 min (90×8s) en vez de
        // ~5 min, para no rendirnos antes de que aterricen los posts.
        const maxTries = isSlowPlatform(plat) ? 90 : 36;
        let lastCount = -1;   // posts_stored del poll anterior (detectar fin del goteo)
        let stable = 0;       // polls consecutivos sin cambio de conteo
        for (let i = 0; i < maxTries && alive(); i++) {
            await sleep(8000);
            if (!alive()) return;
            try {
                // Siempre TODAS las redes: si filtráramos por plat, el carrusel se
                // colapsaría a esa red durante la ingesta (perdiendo los demás chips).
                const r = await socialApi.listAccounts(username);
                if (!alive()) return;
                setAccounts(r.accounts || []);
                // Por id cuando lo tenemos: el handle guardado en la fila canónica
                // puede diferir en mayúsculas del que tecleó este usuario (la cuenta
                // es compartida), y comparar strings dejaba el poll girando en vacío.
                const acc = (r.accounts || []).find(
                    (a) => (accountId ? a.id === accountId
                        : a.username === handleClean && (!plat || a.platform === plat)));
                if (!acc) continue;
                // Cada vuelta re-selecciona -> re-carga overview+posts (datos frescos).
                setSelected(acc);

                // ¿Ya empezó a aterrizar la captura NUEVA? (en orden de preferencia)
                const syncedMs = acc.posts_synced_at ? new Date(acc.posts_synced_at).getTime() : null;
                let started;
                if (syncedMs != null) {
                    // (1) Backend con posts_synced_at: hay una captura MÁS reciente.
                    started = acc.posts_stored > 0 && syncedMs > sinceMs;
                } else if (beforePosts != null) {
                    // (2) Refresh sin posts_synced_at: los posts cambiaron respecto
                    // al estado previo (re-captura confirmada).
                    const rb = isKwAcct(acc)
                        ? { from: '', to: '' } : rangeBounds(rangeKey, customRange);
                    const ps = await socialApi.posts(username, accountId || acc.id, rb.from, rb.to);
                    if (!alive()) return;
                    started = fpPosts(ps.posts) !== beforePosts;
                } else {
                    // (3) Alta nueva: aparecen los primeros posts.
                    started = acc.posts_stored > 0;
                }
                if (!started) { lastCount = acc.posts_stored || 0; continue; }

                // La captura inserta los posts en GOTEO (sube cada miniatura a S3
                // antes de guardar el post), así que NO cortamos al primer post:
                // esperamos a que el conteo se estabilice (~16s sin cambios) para no
                // quedarnos con un set parcial (el bug de "1 post y al recargar salen
                // todos"). El conteo solo crece, así que el plateau = fin de captura.
                const count = acc.posts_stored || 0;
                stable = count === lastCount ? stable + 1 : 0;
                lastCount = count;
                if (stable >= 2) break;
            } catch (_) { /* reintenta */ }
        }
        if (alive()) {
            setIngesting(false);
            // Terminó la captura: pedimos un reload del dashboard marcando que, si el
            // rango activo no muestra nada de lo que se acaba de traer, hay que
            // ampliarlo (el histórico casi siempre cae fuera del mes en curso).
            widenAfterCapture.current = true;
            setSelected((s) => (s ? { ...s } : s));
        }
    }, [username, rangeKey, customRange]);

    // Alta desde el modal: elige red + tipo + handle. Si la red difiere de la
    // activa, cambiamos a ella para mostrar la cuenta recién agregada.
    const submitAdd = async (platformSel, kindSel, val, trackBy = 'profile', nick = '') => {
        // Una URL completa (grupo de FB, perfil de TikTok) se manda como account_url.
        // Las keywords (TikTok modo keyword) nunca son URL aunque contengan '.com/'.
        const looksUrl = trackBy !== 'keyword'
            && (/^https?:\/\//i.test(val) || /facebook\.com\//i.test(val) || /tiktok\.com\//i.test(val)
                || /youtube\.com\//i.test(val) || /youtu\.be\//i.test(val));
        const h = val.replace(/^[@#]/, '');   // limpia @ (handles) o # (keywords TikTok)
        setAdding(true); setAddErr('');
        try {
            const resp = await socialApi.addAccount(
                username, platformSel, looksUrl ? '' : h, looksUrl ? val : '', kindSel, trackBy, nick);
            setAddOpen(false);
            // Mostrar el chip + seleccionar de inmediato, sin esperar al primer poll.
            try {
                const r = await socialApi.listAccounts(username);   // todas las redes
                setAccounts(r.accounts || []);
                // El id es la vía segura: el handle que devuelve el backend es el
                // CANÓNICO y puede no coincidir con el que se tecleó aquí.
                const acc = (r.accounts || []).find((a) => a.id === resp?.account?.id)
                    || (r.accounts || []).find(
                        (a) => a.username === (resp?.account?.username || h) && a.platform === platformSel);
                if (acc) setSelected(acc);
            } catch (_) { /* el poll lo recupera */ }
            // 'fresh' = alguien más ya monitorea esta cuenta y su información está
            // al día: se ve completa desde el primer render, no hay captura que esperar.
            if (resp?.status === 'fresh') { setErr(resp.message || ''); return; }
            pollForData(resp?.account?.username || h,
                { platform: platformSel, accountId: resp?.account?.id });
        } catch (e) { setAddErr(e.message); }   // se muestra dentro del modal (sigue abierto)
        finally { setAdding(false); }
    };

    const onSetKind = async (acc, kind) => {
        if (kindOf(acc) === kind) return;
        // Optimista: la lista se reordena de inmediato.
        setAccounts((prev) => prev.map((a) => (a.id === acc.id ? { ...a, account_kind: kind } : a)));
        if (selected?.id === acc.id) setSelected((s) => ({ ...s, account_kind: kind }));
        try {
            await socialApi.setAccountKind(username, acc.id, kind);
        } catch (e) {
            setErr(e.message);
            await loadAccounts();   // revertir si falló
        }
    };

    const onRemove = async (acc) => {
        if (!window.confirm(`¿Dejar de monitorear ${acctLabel(acc)}?`)) return;
        try {
            await socialApi.removeAccount(username, acc.id);
            if (selected?.id === acc.id) { setSelected(null); setOverview(null); }
            await loadAccounts();
        } catch (e) { setErr(e.message); }
    };

    // Guarda/limpia el sobrenombre de una cuenta (optimista; revierte si falla).
    const onSetNickname = async (acc, nickname) => {
        const nick = (nickname || '').trim().slice(0, 120);
        const value = nick || null;
        setAccounts((prev) => prev.map((a) => (a.id === acc.id ? { ...a, nickname: value } : a)));
        if (selected?.id === acc.id) setSelected((s) => ({ ...s, nickname: value }));
        setNickEdit(null);
        try {
            await socialApi.setNickname(username, acc.id, nick);
        } catch (e) {
            setErr(e.message);
            await loadAccounts();   // revertir si falló
        }
    };

    const onRefreshAccount = async () => {
        if (!selected) return;
        // Huella de los posts actuales: el poll espera a que cambien (re-captura).
        // `posts_synced_at` es la vía rápida si el backend ya lo expone.
        const beforePosts = fpPosts(posts);
        try {
            const resp = await socialApi.refreshAccount(username, selected.id);
            // La cuenta es COMPARTIDA: si otro usuario que la monitorea ya la
            // actualizó hace poco, el backend no vuelve a extraer (ni cobra cupo).
            // No hay captura que esperar: recargamos y avisamos. Con 'running' sí
            // poleamos: la captura de ese otro usuario va en camino.
            if (resp?.status === 'fresh') {
                await loadAccounts();
                // Los datos ya estaban completos (los capturó otro suscriptor): aplica
                // el mismo ensanche de rango que tras una captura propia.
                widenAfterCapture.current = true;
                await loadDashboard();
                setErr(resp.message || 'Los datos ya están actualizados.');
                return;
            }
            pollForData(selected.username, {
                sinceSyncedAt: selected.posts_synced_at,
                beforePosts,
                accountId: selected.id,
                platform: selected.platform,   // desambigua si el handle existe en 2 redes
            });
        } catch (e) { setErr(e.message); }
    };

    // Reordenar chips por arrastre (client-side, persistido en localStorage).
    const persistOrder = (ids) => {
        setOrder(ids);
        try { localStorage.setItem(`sa_account_order_${username}`, JSON.stringify(ids)); } catch (_) { /* ignore */ }
    };
    const onDropChip = (toId) => {
        const fromId = dragId.current;
        dragId.current = null; setDragOverId(null);
        if (fromId == null || fromId === toId) return;
        const ids = applyOrder(accounts, order).map((a) => a.id);
        const from = ids.indexOf(fromId);
        const to = ids.indexOf(toId);
        if (from < 0 || to < 0) return;
        ids.splice(to, 0, ids.splice(from, 1)[0]);
        persistOrder(ids);   // optimista + localStorage (bridge)
        // Persistir en el server (org-wide, cross-device). Si el backend aún no está
        // desplegado, falla en silencio y queda el orden local de localStorage.
        socialApi.reorderAccounts(username, ids).catch(() => { /* bridge local */ });
    };
    const orderedAccounts = applyOrder(accounts, order);
    const visibleAccounts = kindFilter === 'all'
        ? orderedAccounts : orderedAccounts.filter((a) => kindOf(a) === kindFilter);
    // TikTok no usa rango de fechas (histórico por keyword): bounds vacíos = todo.
    const isKw = isKwAcct(selected);
    const range = isKw ? { from: '', to: '' } : rangeBounds(rangeKey, customRange);
    // TikTok-por-keyword está en beta: solo los usuarios en TIKTOK_USERS pueden agregarlo.
    const availablePlatforms = TIKTOK_USERS.includes(username)
        ? PLATFORMS : PLATFORMS.filter((p) => p.id !== 'tiktok');

    const rg = overview?.reach_growth;
    const eng = overview?.engagement;
    const sent = overview?.sentiment;
    // Flujo de NSS por fecha de PUBLICACIÓN (mismo dato que el chart, badges y
    // drill-down). Lo usamos también para los insights → todo queda consistente.
    const flow = buildPostFlow(posts, flowBucket);

    // Insights por publicación (alineados con el chart): tendencia + NSS promedio.
    const insights = [];
    let trendAlert = false;
    if (flow.length >= 2) {
        const last = flow[flow.length - 1];
        const avgPrev = flow.slice(0, -1).reduce((s, b) => s + b.nss, 0) / (flow.length - 1);
        const diff = Math.round((last.nss - avgPrev) * 100) / 100;   // + subió, − bajó
        const periodo = flowBucket === 'day' ? 'del último día' : 'de la última semana';
        if (diff <= -25) {
            trendAlert = true;
            insights.push({ tone: 'neg', text: `Posible baja: el NSS de tus publicaciones ${periodo} cayó ${Math.abs(diff)} pts vs. el promedio reciente (NSS ${last.nss}).` });
        } else if (diff > 0) {
            insights.push({ tone: 'pos', text: `El NSS de tus publicaciones ${periodo} subió ${diff} pts vs. el promedio reciente (NSS ${last.nss}).` });
        } else if (diff < 0) {
            insights.push({ tone: 'neu', text: `El NSS de tus publicaciones ${periodo} bajó ${Math.abs(diff)} pts vs. el promedio reciente (NSS ${last.nss}).` });
        } else {
            insights.push({ tone: 'neu', text: `El NSS de tus publicaciones ${periodo} se mantuvo estable (NSS ${last.nss}).` });
        }
    }
    const analyzedPosts = posts.filter((p) => p.nss != null);
    if (analyzedPosts.length) {
        const avg = Math.round((analyzedPosts.reduce((s, p) => s + p.nss, 0) / analyzedPosts.length) * 100) / 100;
        insights.push({ tone: avg > 15 ? 'pos' : avg < -15 ? 'neg' : 'neu', text: `NSS promedio ${avg} sobre ${analyzedPosts.length} publicaciones analizadas.` });
    }
    if (rg?.followers_growth != null && rg.followers_growth !== 0) {
        const up = rg.followers_growth > 0;
        insights.push({ tone: up ? 'pos' : 'neg', text: `Followers ${up ? '+' : '−'}${fmt(Math.abs(rg.followers_growth))} en el rango seleccionado.` });
    }
    if (!insights.length) {
        insights.push({ tone: 'neu', text: 'Analiza el sentimiento de algunos posts para ver insights aquí.' });
    }
    const flowLabels = flow.map((b) => (b.bucket || '').slice(0, 10));
    const nssRaw = flow.map((b) => b.nss);
    const nssLine = smooth ? movingAvg(nssRaw, 3) : nssRaw;
    const flowDatasets = [{
        label: smooth ? 'Tendencia' : 'NSS', data: nssLine,
        borderColor: '#073C5C', backgroundColor: 'rgba(7,60,92,.12)',
        fill: true, tension: smooth ? 0.45 : 0.3,
        pointRadius: 4, pointBackgroundColor: '#073C5C', order: 1,
    }];
    if (smooth) {
        flowDatasets.push({
            label: 'Real', data: nssRaw,
            borderColor: 'rgba(7,60,92,.22)', backgroundColor: 'transparent',
            fill: false, tension: 0, borderDash: [4, 4],
            pointRadius: flowBucket === 'day' ? 1.5 : 3, pointBackgroundColor: 'rgba(7,60,92,.3)',
            showLine: flowBucket !== 'day', order: 2,
        });
    }

    // Drill-down: publicaciones del día/semana clickeado (por fecha de PUBLICACIÓN).
    // Comparamos por el slice UTC 'YYYY-MM-DD' para no desfasar por zona horaria.
    const dayEnd = selectedDay ? addDaysStr(selectedDay, flowBucket === 'day' ? 1 : 7) : null;
    const dayPosts = selectedDay
        ? posts.filter((p) => {
            const d = (p.posted_at || '').slice(0, 10);
            return d && d >= selectedDay && d < dayEnd;
        })
        : [];

    return (
        <div className="sa-accounts-v2">
            {/* Filtro discreto por tipo, encima del carrusel (toggle: click de nuevo lo quita) */}
            <div className="sa-kind-filter">
                {Object.values(KINDS).map((k) => {
                    const count = accounts.filter((a) => kindOf(a) === k.key).length;
                    const active = kindFilter === k.key;
                    return (
                        <button key={k.key} type="button"
                            className={`sa-kind-pill ${active ? 'active' : ''}`}
                            style={active ? { borderColor: k.color, color: k.color } : undefined}
                            onClick={() => setKindFilter(active ? 'all' : k.key)}
                            title={active ? 'Quitar filtro' : `Ver solo ${k.plural.toLowerCase()}`}>
                            <i style={{ background: k.color }} /> {k.plural} <em>{count}</em>
                        </button>
                    );
                })}
            </div>

            {/* Barra superior: carrusel de cuentas + alta */}
            <header className="sa-topbar">
                <div className="sa-account-carousel">
                    {!SOCIAL_READONLY && (
                        <button className="sa-add-chip" onClick={() => { setAddErr(''); setAddOpen(true); }}>
                            <FaPlus /> Agregar cuenta
                        </button>
                    )}
                    {visibleAccounts.map((a) => {
                        const kc = KINDS[kindOf(a)].color;
                        const active = selected?.id === a.id;
                        const ap = PLATFORMS.find((x) => x.id === a.platform) || PLATFORMS[0];
                        const AIcon = ap.icon;
                        return (
                            <div key={a.id}
                                className={`sa-chip ${active ? 'active' : ''} ${dragOverId === a.id ? 'dragover' : ''}`}
                                style={{ '--kind-color': kc }}
                                draggable
                                onDragStart={(e) => { dragId.current = a.id; e.dataTransfer.effectAllowed = 'move'; }}
                                onDragEnter={() => setDragOverId(a.id)}
                                onDragOver={(e) => e.preventDefault()}
                                onDragEnd={() => { dragId.current = null; setDragOverId(null); }}
                                onDrop={(e) => { e.preventDefault(); onDropChip(a.id); }}
                                onClick={() => setSelected(a)}>
                                <span className="sa-chip-dot" />
                                <AIcon className="sa-chip-net" style={{ color: ap.color }} />
                                <span className="sa-chip-handle">{acctLabel(a)}</span>
                                {!SOCIAL_READONLY && (
                                    <button className="sa-chip-caret" title="Acciones"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (menu?.id === a.id) { setMenu(null); return; }
                                            const r = e.currentTarget.getBoundingClientRect();
                                            setMenu({ id: a.id, x: r.right, y: r.bottom });
                                        }}>
                                        <FaChevronDown />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {accounts.length === 0 && (
                        <span className="sa-chip-empty">Aún no hay cuentas.</span>
                    )}
                    {accounts.length > 0 && visibleAccounts.length === 0 && (
                        <span className="sa-chip-empty">Sin cuentas de este tipo.</span>
                    )}
                </div>
            </header>

            {/* Dashboard */}
            <section className="sa-dashboard">
                {ingesting && (
                    <div className="sa-banner sa-banner-info">
                        <FaSync className="spin" /> Capturando datos de la cuenta…
                        {isSlowPlatform(selected?.platform)
                            ? ' Puede tardar hasta ~10 min. Si no aparece, pulsa Actualizar en un momento.'
                            : ' (~1 min)'}
                    </div>
                )}
                {err && <div className="sa-banner sa-banner-info">{err}</div>}
                {!selected && <div className="sa-placeholder">Selecciona o agrega una cuenta para ver su analítica.</div>}

                {selected && (
                    <>
                        <div className="sa-dash-head">
                            <h3>
                                {(() => {
                                    const p = PLATFORMS.find((x) => x.id === selected.platform) || PLATFORMS[0];
                                    const Icon = p.icon;
                                    return <Icon style={{ color: p.color }} />;
                                })()} {acctLabel(selected)}
                                {(() => {
                                    const k = KINDS[kindOf(selected)];
                                    const KIcon = k.Icon;
                                    return (
                                        <span className="sa-kind-badge"
                                            style={{ color: k.color, borderColor: k.color }}>
                                            <KIcon /> {k.label}
                                        </span>
                                    );
                                })()}
                            </h3>
                            <div className="sa-dash-actions">
                                {isKw ? (
                                    <span className="sa-kw-hint" title="TikTok por keyword: se muestra todo el histórico encontrado">
                                        <FaRegCalendarAlt /> Histórico completo
                                    </span>
                                ) : (
                                    <DateRangeControl rangeKey={rangeKey} customRange={customRange}
                                        onPreset={(k) => setRangeKey(k)}
                                        onCustom={(from, to) => { setCustomRange({ from, to }); setRangeKey('custom'); }} />
                                )}
                                {!SOCIAL_READONLY && (
                                    <button onClick={onRefreshAccount} title="Re-capturar perfil y posts">
                                        <FaSync /> Actualizar
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="sa-kpis">
                            {isKwAcct(selected) ? (
                                <div className="sa-kpi">
                                    <FaPlay /><span className="sa-kpi-val">{fmt(selected.posts_stored ?? posts.length)}</span>
                                    <span className="sa-kpi-lbl">Publicaciones</span>
                                </div>
                            ) : (
                                <div className="sa-kpi">
                                    <FaUsers /><span className="sa-kpi-val">{fmt(rg?.followers)}</span>
                                    <span className="sa-kpi-lbl">Followers
                                        {rg?.followers_growth != null && (
                                            <em className={rg.followers_growth >= 0 ? 'up' : 'down'}>
                                                {rg.followers_growth >= 0 ? '▲' : '▼'} {fmt(Math.abs(rg.followers_growth))}
                                            </em>)}
                                    </span>
                                </div>
                            )}
                            <div className="sa-kpi">
                                <FaHeart /><span className="sa-kpi-val">{eng?.avg_engagement_rate ?? '—'}%</span>
                                <span className="sa-kpi-lbl">Engagement rate</span>
                            </div>
                            <div className="sa-kpi">
                                <FaChartLine />
                                <span className="sa-kpi-val" style={{ color: nssColor(sent?.nss) }}>{sent?.nss ?? '—'}</span>
                                <span className="sa-kpi-lbl">NSS (sentimiento)</span>
                            </div>
                            <div className="sa-kpi">
                                <FaCommentDots /><span className="sa-kpi-val">{fmt(sent?.analyzed)}</span>
                                <span className="sa-kpi-lbl">Comentarios analizados</span>
                            </div>
                        </div>

                        {loadingDash && <div className="sa-loading">Cargando métricas…</div>}

                        <div className="sa-card">
                            <div className="sa-flow-head">
                                <h4>NSS por publicación ({flowBucket === 'day' ? 'día' : 'semana'} de publicación)</h4>
                                <div className="sa-flow-controls">
                                    <div className="sa-seg">
                                        <button className={flowBucket === 'week' ? 'active' : ''}
                                            onClick={() => setFlowBucket('week')}>Semana</button>
                                        <button className={flowBucket === 'day' ? 'active' : ''}
                                            onClick={() => setFlowBucket('day')}>Día</button>
                                    </div>
                                    <button className={`sa-smooth-toggle ${smooth ? 'active' : ''}`}
                                        onClick={() => setSmooth((s) => !s)}
                                        title="Suavizar la línea con media móvil">
                                        <FaChartLine /> Suavizar
                                    </button>
                                </div>
                            </div>
                            {flow.length === 0 ? <p className="sa-empty">Aún no hay posts con sentimiento analizado. Abre un post y pulsa "Analizar sentimiento" para que aparezca aquí.</p> : (
                                <>
                                    <Line
                                        data={{ labels: flowLabels, datasets: flowDatasets }}
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: {
                                                    display: smooth, position: 'top',
                                                    labels: { boxWidth: 12, font: { size: 11 } },
                                                },
                                            },
                                            interaction: { intersect: false, mode: 'index' },
                                            scales: { y: { min: -100, max: 100 } },
                                            onClick: (_e, els) => {
                                                if (els && els.length) setSelectedDay(flowLabels[els[0].index]);
                                            },
                                            onHover: (e, els) => {
                                                const t = e?.native?.target;
                                                if (t) t.style.cursor = els && els.length ? 'pointer' : 'default';
                                            },
                                        }} height={90} />
                                    <p className="sa-flow-hint">
                                        {flow.length === 1
                                            ? 'Analiza el sentimiento de posts de otras fechas para ver la tendencia.'
                                            : 'Cada punto = promedio del NSS de los posts publicados ese ' + (flowBucket === 'day' ? 'día' : 'semana') + '. Haz click para verlos.'}
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="sa-three-col">
                            {/* Engagement: se queda igual (barra por tipo de publicación) */}
                            <div className="sa-card">
                                <h4>Engagement por tipo</h4>
                                {(eng?.by_content_type?.length) ? (
                                    <Bar
                                        data={{
                                            labels: eng.by_content_type.map((t) => t.content_type),
                                            datasets: [{
                                                label: 'ER %', data: eng.by_content_type.map((t) => t.avg_er),
                                                backgroundColor: '#F68D2E',
                                            }],
                                        }}
                                        options={{ responsive: true, plugins: { legend: { display: false } } }}
                                        height={140} />
                                ) : <p className="sa-empty">Sin posts en el rango.</p>}
                            </div>

                            {/* Temas más comentados: lista limpia (sin flechas), con % de participación */}
                            <div className="sa-card">
                                <h4>Temas más comentados</h4>
                                {(() => {
                                    const ts = overview?.themes || [];
                                    const total = ts.reduce((s, t) => s + (t.n || 0), 0) || 1;
                                    if (!ts.length) return <p className="sa-empty">Sin temas aún.</p>;
                                    return (
                                        <ul className="sa-topic-list">
                                            {ts.map((th, i) => (
                                                <li key={i} onClick={() => setOpenTheme(th.theme)}
                                                    title="Ver comentarios de este tema">
                                                    <span className="sa-topic-dot" style={{ background: nssColor(th.nss) }} />
                                                    <span className="sa-topic-name" title={th.theme}>{th.theme}</span>
                                                    <span className="sa-topic-pct"
                                                        title="Participación entre los temas detectados">
                                                        {Math.round((th.n / total) * 100)}%</span>
                                                </li>
                                            ))}
                                        </ul>
                                    );
                                })()}
                            </div>

                            {/* Insights destacados: aquí vive la señal de crisis/tendencia */}
                            <div className={`sa-card sa-insights-card ${trendAlert ? 'crisis' : ''}`}>
                                <h4>{trendAlert ? <FaExclamationTriangle /> : <FaMagic />} Insights destacados</h4>
                                <div className="sa-insights-list">
                                    {insights.slice(0, 3).map((ins, i) => (
                                        <div key={i} className="sa-insight">
                                            <i style={{ background: TONE[ins.tone] }} />
                                            <span>{ins.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Posts: tabla (default) o grid de cards */}
                        <div className="sa-card">
                            <div className="sa-flow-head">
                                <h4>Posts ({posts.length})</h4>
                                <div className="sa-flow-controls">
                                    <select className="sa-sort-select" value={postsSort}
                                        onChange={(e) => setPostsSort(e.target.value)}
                                        title="Ordenar publicaciones">
                                        {POST_SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                                    </select>
                                    <div className="sa-seg">
                                        <button className={postsView === 'table' ? 'active' : ''}
                                            onClick={() => setPostsView('table')}>Tabla</button>
                                        <button className={postsView === 'grid' ? 'active' : ''}
                                            onClick={() => setPostsView('grid')}>Grid</button>
                                    </div>
                                </div>
                            </div>
                            {posts.length === 0 ? (
                                // "Sin posts capturados" mentía cuando la cuenta sí tiene
                                // histórico y lo que pasa es que cae fuera del rango (el
                                // default es el mes en curso, así que casi siempre).
                                !isKw && (selected.posts_stored || 0) > 0 ? (
                                    <p className="sa-empty">
                                        Sin publicaciones en este rango. Esta cuenta tiene {fmt(selected.posts_stored)} capturadas.{' '}
                                        <button type="button" className="sa-empty-link"
                                            onClick={() => setRangeKey('all')}>Ver todo</button>
                                    </p>
                                ) : (
                                    <p className="sa-empty">Sin posts capturados.</p>
                                )
                            ) : postsView === 'grid' ? (
                                <div className="sa-post-grid">
                                    {sortPosts(posts, postsSort).map((p) => <PostCard key={p.id} post={p} onOpen={setOpenPost} onToggleFeatured={toggleFeatured} onHide={hidePost} />)}
                                </div>
                            ) : (
                                <PostsTable posts={sortPosts(posts, postsSort)} onOpen={setOpenPost} onToggleFeatured={toggleFeatured} onHide={hidePost} />
                            )}
                        </div>

                        {/* Publicaciones destacadas (privadas del usuario) de la cuenta activa */}
                        {destacados.length > 0 && (
                            <div className="sa-card">
                                <h4><FaStar style={{ color: '#f5b301' }} /> Publicaciones destacadas ({destacados.length})</h4>
                                <div className="sa-post-grid">
                                    {destacados.map((p) => (
                                        <PostCard key={p.id} post={p} onOpen={setOpenPost} onToggleFeatured={toggleFeatured} onHide={hidePost} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* Menú de acciones del chip (posición fija para no recortarse en el carrusel) */}
            {!SOCIAL_READONLY && menu && (() => {
                const a = accounts.find((x) => x.id === menu.id);
                if (!a) return null;
                const other = kindOf(a) === 'own' ? 'interest' : 'own';
                return (
                    <div className="sa-chip-menu" onClick={(e) => e.stopPropagation()}
                        style={{ top: Math.min(menu.y + 6, window.innerHeight - 148), left: Math.max(8, menu.x - 188) }}>
                        <button onClick={() => { setNickEdit({ id: a.id, value: a.nickname || '' }); setMenu(null); }}>
                            <FaPen /> Editar sobrenombre
                        </button>
                        <button onClick={() => { setMenu(null); onSetKind(a, other); }}>
                            <FaExchangeAlt /> Mover a {KINDS[other].label}
                        </button>
                        <button className="danger" onClick={() => { setMenu(null); onRemove(a); }}>
                            <FaTrash /> Quitar
                        </button>
                    </div>
                );
            })()}

            {/* Editor de sobrenombre (alias): se muestra en el carrusel en vez del nombre real */}
            {nickEdit && (() => {
                const a = accounts.find((x) => x.id === nickEdit.id);
                if (!a) return null;
                return (
                    <div className="sa-modal-overlay" onClick={() => setNickEdit(null)}>
                        <div className="sa-modal" onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: 420, padding: 24 }}>
                            <button className="sa-modal-close" onClick={() => setNickEdit(null)}><FaTimes /></button>
                            <h4 style={{ margin: '0 0 4px' }}>Sobrenombre de la cuenta</h4>
                            <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>
                                {acctHandle(a)} · {PLATFORMS.find((p) => p.id === a.platform)?.label || a.platform}
                            </p>
                            <input autoFocus maxLength={120} value={nickEdit.value}
                                placeholder="Ej. Canal oficial de YouTube"
                                onChange={(e) => setNickEdit((s) => ({ ...s, value: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') onSetNickname(a, nickEdit.value); }}
                                style={{
                                    width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1',
                                    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
                                }} />
                            <p style={{ margin: '8px 0 16px', color: '#94a3b8', fontSize: 12 }}>
                                Se muestra en el carrusel en vez del nombre real. Déjalo vacío para usar el nombre original.
                            </p>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button onClick={() => setNickEdit(null)}
                                    style={{
                                        padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1',
                                        background: '#fff', cursor: 'pointer',
                                    }}>Cancelar</button>
                                <button onClick={() => onSetNickname(a, nickEdit.value)}
                                    style={{
                                        padding: '8px 14px', borderRadius: 8, border: 'none',
                                        background: '#073C5C', color: '#fff', cursor: 'pointer', fontWeight: 600,
                                    }}>Guardar</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {addOpen && (
                <AddAccountModal initialPlatform={selected?.platform || 'instagram'} busy={adding}
                    err={addErr} platforms={availablePlatforms}
                    onClose={() => setAddOpen(false)} onSubmit={submitAdd} />
            )}

            {selectedDay && (
                <div className="sa-modal-overlay" onClick={() => setSelectedDay(null)}>
                    <div className="sa-modal sa-day-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="sa-modal-close" onClick={() => setSelectedDay(null)}><FaTimes /></button>
                        <div className="sa-day-modal-body">
                            <h4>
                                Publicaciones del {selectedDay}
                                {flowBucket === 'week' ? ` al ${addDaysStr(selectedDay, 6)}` : ''}
                                <span className="sa-day-count"> · {dayPosts.length}</span>
                            </h4>
                            {dayPosts.length === 0 ? (
                                <p className="sa-empty">
                                    No se publicaron posts {flowBucket === 'day' ? 'ese día' : 'esa semana'}.
                                    El sentimiento de {flowBucket === 'day' ? 'ese día' : 'esa semana'} proviene de
                                    comentarios sobre publicaciones anteriores.
                                </p>
                            ) : (
                                <div className="sa-post-grid">
                                    {dayPosts.map((p) => <PostCard key={p.id} post={p} onOpen={setOpenPost} />)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {openTheme && selected && (
                <ThemeModal username={username} accountId={selected.id} theme={openTheme}
                    dateFrom={range.from} dateTo={range.to} onOpenPost={setOpenPost}
                    onClose={() => setOpenTheme(null)} />
            )}

            {openPost && (
                <PostModal username={username} platform={selected?.platform || 'instagram'} post={openPost}
                    onClose={() => setOpenPost(null)} onChanged={loadDashboard} />
            )}
        </div>
    );
}
